import { ChangeDetectionStrategy, Component, Input, ChangeDetectorRef, OnInit, OnDestroy } from "@angular/core";
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/delay';

import swal from "bootstrap-sweetalert";
import { Subscription } from "rxjs/Subscription";
import { ActivatedRoute, Router } from "@angular/router";
import { BsModalRef, BsModalService } from "ngx-bootstrap";
import { ServiceObjectSearchComponent } from "app/client/serviceObject/search/serviceObject-search.component";
import { ScrollToAnchorService } from "app/services/scroll-to-anchor.service";
import { PagedResult } from "app/models/PagedResult";
import { ServiceObject } from "app/models/serviceObject";
import { ServiceObjectService } from "app/services/serviceObject.service";

@Component({
  templateUrl: './location-list.component.html',
  styleUrls: ['./location-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationListComponent implements OnInit, OnDestroy {

  constructor(
    private _serviceObjectService: ServiceObjectService,
    private _changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private modalService: BsModalService,
    private _scrollToAnchorService: ScrollToAnchorService
  ) { }

  private _serviceObjects: ServiceObject[];
  private _serviceObjectsObservable: Observable<PagedResult<ServiceObject>>;
  private _serviceObjectsSubscription: Subscription;

  private _pagination = {
    id: 'locations_pagination',
    itemsPerPage: 20,
    currentPage: 1,
    totalItems: 0
  }
  private sortBy = "Name";
  private isDesc = false;
  private _errorState: LocationListComponent.ErrorState = LocationListComponent.ErrorState.None;
  private serviceObjectModalRef: BsModalRef;

  ngOnInit() {
    this.loadPageWithUrlFilterParams();
  }

  ngOnDestroy(): void {
    this.clearSubscription();

    swal.close();

    if(this.serviceObjectModalRef) {
      this.serviceObjectModalRef.hide();
    }
  }

  private setItemsPerPage(itemsPerPage: number): void {
    this.setQueryParams({ count: itemsPerPage });
  }

  private setPage(page: number) {
    this.setQueryParams({ page: page });
  }

  private sort(sortBy: string, isDesc: boolean) {
    this.setQueryParams(
      {
        sortBy: sortBy,
        isDesc: isDesc
      });
  }

  private loadPageWithUrlFilterParams() {
    this.activatedRoute.queryParams.subscribe(params => {
      this.isDesc = params['isDesc'] ? JSON.parse(params['isDesc']) : this.isDesc;
      this.sortBy = params['sortBy'] ? params['sortBy'] : this.sortBy;
      this._pagination.itemsPerPage = params['count'] ? params['count'] : this._pagination.itemsPerPage;
      this._pagination.currentPage = params['page'] ? params['page'] : this._pagination.currentPage;

      this.clearSubscription();

      this.clearSubscription();

      this.loadData();
    });
  }

  loadData() {
    this._errorState = LocationListComponent.ErrorState.None;
    // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
    this._changeDetector.markForCheck();

    this.clearSubscription();

    let newServiceObjects = this._serviceObjectService
      .getServiceObjectsPerPage(
        this.sortBy,
        this.isDesc,
        this._pagination.itemsPerPage,
        (this._pagination.currentPage - 1) * this._pagination.itemsPerPage);

    this._serviceObjectsObservable = newServiceObjects;

    let itemsObservable = this._serviceObjectsObservable
      .subscribe(res => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (this._serviceObjectsObservable === newServiceObjects) {
          this._serviceObjects = res.items;
          this._pagination.totalItems = res.totalCount;
          this._changeDetector.markForCheck();
          this.restoreScrollPosition();
        }
      }, error => {
        if (error.status == 403 || error.status == 404) {
          this.router.navigateByUrl('/pagenotfound');
        } else {
          // если на форме сейчас те элементы, которыми управляет данный Observable
          if (this._serviceObjectsObservable === newServiceObjects) {
            this._errorState = LocationListComponent.ErrorState.UnknownError;
            // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
            this._changeDetector.markForCheck();

            swal({
              title: 'Ошибка',
              text: 'Не удалось получить данные объектов обслуживания. Попробуйте снова',
              type: 'error',
              confirmButtonText: 'Попробовать еще раз'
            }, () => {
              this.loadData();
            });
          }
        }
      });
  }

  private clearSubscription() {
    if (this._serviceObjectsSubscription)
      this._serviceObjectsSubscription.unsubscribe();

    this._serviceObjectsSubscription = null;
    this._serviceObjectsObservable = null;
    this._serviceObjects = null;
  }

  private openServiceObjectSearchModal() {
    this.serviceObjectModalRef = this.modalService.show(ServiceObjectSearchComponent, { class: 'modal-lg', backdrop: 'static' });
  }

  private goToServiceObjectChildList(elementId : string, serviceObjectId : number) {
    let currentUrl = this.router.url.split('#')[0];
    this.router.navigate([`/serviceObject/${serviceObjectId}/assets`]);
    window.history.replaceState({}, '', currentUrl + "#" + elementId);
  }

  private goToServiceObjectDetails(elementId : string, serviceObjectId : number) {
    let currentUrl = this.router.url.split('#')[0];
    this.router.navigate([`/serviceObject/${serviceObjectId}`]);
    window.history.replaceState({}, '', currentUrl + "#" + elementId);
  }

  private restoreScrollPosition() : void {
    const fragment = this.activatedRoute.snapshot.fragment;

    if (fragment) {
      let index = parseInt(fragment.split('_')[1]);
      if (index < this._serviceObjects.length) {
        this._scrollToAnchorService.scroll(fragment);
      } else {
        this._scrollToAnchorService.scroll("row_" + (this._serviceObjects.length - 1));
      }
    }
  }

  private setQueryParams(queryParams: object) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        ...queryParams
      }
    });
  }

  private _locationsStatInfoLoadSubscription: Subscription;
  private ErrorStateEnum = LocationListComponent.ErrorState;
}

export namespace LocationListComponent {

  export enum ErrorState {
    /**
     * Нет ошибок
     */
    None,
    /**
     * Другая ошибка при получении данных с сервера
     */
    UnknownError
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/client/serviceObject/location/list/location-list.component.ts